import { memo, useMemo } from 'react';
import { useThree } from '@react-three/fiber';
import { Line } from '@react-three/drei';
import * as THREE from 'three';
import { useCameraTick } from './cameraTick';
import { dispatchEntityClick } from './toolEventBus';
import type { ThreeEvent } from '@react-three/fiber';

/**
 * Infinite construction geometry rendered clipped to the camera (report item
 * [4.4]): XLINE/RAY no longer store fake 500/1000-unit segments at the
 * render edge — the visible endpoints are recomputed from the current camera
 * every render tick, so the line truly spans the drawing forever.
 *
 * `ray` extends in one direction from the base point; `xline` in both.
 */
export const InfiniteLineEntity = memo(function InfiniteLineEntity({
    start,
    end,
    ray = false,
    color = '#666666',
    lineWidth = 1.5,
    entityId,
    dashed,
    dashSize = 0.5,
    gapSize = 0.25,
}: {
    start: number[];
    end: number[];
    ray?: boolean;
    color?: string;
    lineWidth?: number;
    entityId: string;
    dashed?: boolean;
    dashSize?: number;
    gapSize?: number;
}) {
    const camera = useThree((s) => s.camera);
    const gl = useThree((s) => s.gl);
    // Re-render whenever the viewport camera pans/zooms (engine dispatches).
    const tick = useCameraTick();

    const points = useMemo(() => {
        const dx = end[0] - start[0], dy = end[1] - start[1];
        const len = Math.hypot(dx, dy);
        if (len < 1e-9) return null;
        const ux = dx / len, uy = dy / len;
        // Half-extent of the visible ground region generously rounded up.
        let half = 200;
        const canvas = gl.domElement;
        const hPx = canvas?.clientHeight || 800, wPx = canvas?.clientWidth || 1200;
        const ortho = camera as THREE.OrthographicCamera;
        if (ortho.isOrthographicCamera) {
            half = (Math.max(hPx, wPx) / Math.max(ortho.zoom, 0.001)) * 1.5 + 10;
        } else {
            const persp = camera as THREE.PerspectiveCamera;
            const dist = Math.abs(persp.position.z - (start[2] ?? 0)) || 10;
            half = (2 * dist * Math.tan(((persp.fov ?? 50) * Math.PI / 180) / 2) * (wPx / hPx)) * 1.5 + 10;
        }
        const z = start[2] ?? 0;
        const a: [number, number, number] = ray
            ? [start[0], start[1], z]
            : [start[0] - ux * half, start[1] - uy * half, z];
        const b: [number, number, number] = ray
            ? [start[0] + ux * half * 2, start[1] + uy * half * 2, z]
            : [start[0] + ux * half, start[1] + uy * half, z];
        return [a, b] as [number, number, number][];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [camera, gl, start[0], start[1], end[0], end[1], ray, tick]);

    if (!points) return null;
    return (
        <Line
            points={points}
            color={color}
            lineWidth={lineWidth}
            dashed={dashed}
            dashSize={dashSize}
            gapSize={gapSize}
            onClick={(e: ThreeEvent<MouseEvent>) => {
                e.stopPropagation();
                dispatchEntityClick(entityId, e);
            }}
        />
    );
});
